import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  buildAisObservations,
  matchAisObservation,
  AisObservationMatch,
  AisObservationView,
} from '../../models/ais-observations.model';

/** One observation and the vessels on the map that answer it. */
export interface AisObservationBlock {
  view: AisObservationView;
  matches: AisObservationMatch[];
  /**
   * Which of the three nothing-found cases this is — they are NOT the same
   * question and must not share a message:
   *   idle   — no snapshot is drawn, so nothing has been searched
   *   closed — no snapshot drawn and the TOI has ended; nothing more is coming
   *   nomatch— a snapshot IS drawn and this hull is not in it
   */
  empty: 'idle' | 'closed' | 'nomatch' | null;
}

/** What the row's centre action hands back to the host. */
export interface AisObservationFocus {
  observation: any;
  match: AisObservationMatch;
}

/**
 * AIS "Observations" — a tip-and-cue task's trigger, as a panel section.
 *
 * Shaped like Voyage Radar's Vessel Tracks on purpose: the HEADER carries what
 * is being watched for (the observation itself, where its siblings carry a bare
 * count) and the BODY carries what was found (the vessels in the drawn snapshot
 * that answer it). Collapsed, that one line is the whole answer — "watching
 * MMSI 636092371, one on screen".
 *
 * SHARED between AIS providers, because the thing it renders is: the four AIS
 * observations are offered to EVERY open-water AIS tipping sensor and no vendor
 * narrows them (`operators-service/ais/index.js`), so a per-vendor copy of this
 * section could only ever drift from the others. Nothing in this file names a
 * provider — what is drawn, whether these matches are what the map is showing,
 * what "centre on this vessel" means and where the snapshot list lives are all
 * the HOST's answers, handed in as inputs and taken back as outputs. Voyage
 * Radar is the first to mount it; the mount is one element in the host template
 * plus the wires below it.
 *
 * WHAT IT DOES NOT CLAIM. Whether the task has TIPPED is written to
 * `Task.observationMatch` by the cron sweep, and this screen reads the
 * Collection document, which does not carry it. So every string here is about
 * the snapshot on the map — "seen in the 11:54 snapshot", never "tipped" — and
 * the empty states say the task keeps watching regardless of what this list
 * shows. Adding the verdict means adding those two fields to
 * `GET /tasks/getcollection`, not inferring it here.
 *
 * It replaces, for whichever operator mounts it, the legacy Observations table
 * in the host template: a checkbox that flew the map to the hull and opened a
 * blocking dialog when it could not, an ⓘ image whose menu printed the value
 * under the label "Observation value", and a disabled state with nothing saying
 * it was waiting on a loaded snapshot. Every one of those three is now a state
 * in this section. An operator that has not been wired up keeps the table.
 *
 * Purely presentational, like its siblings: no Leaflet, no API. The host feeds
 * the drawn features in and wires the outputs to the map controller, so the
 * section and the map can never disagree about what is on screen.
 *
 * Change detection stays Default to match its siblings. The memoisation below
 * keys on the FEATURE ARRAY IDENTITY, which is the one thing a host must honour
 * to mount this: hand over a new array when the map changes and the same one
 * when it does not. Voyage Radar's snapshot controller already works that way
 * (`currentFeatures` is replaced wholesale, never mutated).
 */
@Component({
  selector: 'app-ais-observations',
  templateUrl: './ais-observations.component.html',
  styleUrls: ['./ais-observations.component.scss'],
})
export class AisObservationsComponent {
  /** The sensor whose `Observation` records this renders — any AIS provider's. */
  @Input() sensor: any;

  /**
   * The features currently DRAWN — the live snapshot, the history pick, or the
   * playback frame on screen. Searching only these is deliberate: the list then
   * always describes what the user can see, and stepping through Refresh
   * History re-answers the question per snapshot.
   */
  @Input() features: any[] = [];

  /** "11:54 UTC" — which snapshot the body searched, or null when none is up. */
  @Input() snapshotLabel: string | null = null;

  /** TOI closed: no further snapshots will be collected. */
  @Input() expired = false;

  /** "20 Aug 2026, 18:00 UTC" — when the window closed. */
  @Input() closeLabel: string | null = null;

  /** MMSI of the vessel the map has selected, so the row can say which. */
  @Input() activeMmsi: string | null = null;

  /**
   * Is THIS section what the map is drawing?
   *
   * The panel's two vessel-bearing sections — this one and the host's snapshot
   * archive — are mutually exclusive on the map: one set of hulls is drawn at a
   * time and the sections say which. So this is not "the section is expanded",
   * it is "the matched vessels below are the vessels on screen"; picking a
   * snapshot elsewhere turns it off without touching the list.
   */
  @Input() onMap = false;

  /**
   * Centre the map on this vessel and select it — the row's primary act.
   *
   * NOT named `focus`: an output by that name is bound with `(focus)`, which is
   * also a native DOM event, and the two are indistinguishable at the call site.
   */
  @Output() focusVessel = new EventEmitter<AisObservationFocus>();

  /**
   * Put this section's matches on the map, or take them off — the toggle that
   * arbitrates between Observations and the snapshot archive.
   *
   * A DEDICATED control rather than the header's click, which stays
   * expand/collapse: what is drawn and what is unfolded are different questions
   * and a task is routinely read with the section collapsed and its vessels up.
   */
  @Output() toggleOnMap = new EventEmitter<void>();

  /**
   * Frame the observed area — a `vessel_location` observation's own geometry.
   *
   * Replaces the show/hide area toggle this section used to carry. The area is
   * no longer something to switch on: the host draws it with the sensor, so the
   * only thing left to ask for is a look at it.
   */
  @Output() centreArea = new EventEmitter<AisObservationView>();

  /** Open the snapshot archive — the way out of the two empty states. */
  @Output() browseSnapshots = new EventEmitter<void>();

  /**
   * Expanded by default. Unlike Filter Vessel Types this is normally one short
   * card, and it is the answer to "what is this task for" — collapsing it by
   * default would hide the section's whole content to save two rows.
   */
  expanded = true;

  private _obsCacheKey = '';
  private _views: AisObservationView[] = [];
  private _blockFeatures: any = null;
  private _blockViews: AisObservationView[] | null = null;
  /** In the key because it picks between two of the three empty states. */
  private _blockExpired: boolean | null = null;
  private _blocks: AisObservationBlock[] = [];

  /** The observation records, in stored order. */
  get observations(): any[] {
    return Array.isArray(this.sensor?.Observation) ? this.sensor.Observation : [];
  }

  /**
   * The renderable observations, memoised — the template reads the blocks
   * below several times per change-detection pass and the host repaints on
   * every map move. Keyed on value and kind rather than on array identity: the
   * host rebuilds `collections` on a poll, handing over an equal array with a
   * new reference.
   */
  get views(): AisObservationView[] {
    const records = this.observations;
    const key = records
      .map((o: any) => `${o?.key ?? o?.name ?? ''}=${o?.observationValue ?? ''}#${o?.action ?? ''}`)
      .join('|');
    if (key !== this._obsCacheKey) {
      this._obsCacheKey = key;
      this._views = buildAisObservations(records);
      // Force the block rebuild below: the views it matched with are gone.
      this._blockViews = null;
    }
    return this._views;
  }

  /**
   * One block per observation. Memoised on the FEATURE ARRAY IDENTITY, which
   * is exact rather than approximate: the snapshot controller replaces
   * `currentFeatures` wholesale on every pull, history pick and playback frame,
   * and never mutates it in place — so a new reference is precisely "the map
   * changed", and an unchanged one is precisely "nothing to re-match".
   */
  get blocks(): AisObservationBlock[] {
    const views = this.views;
    const features = Array.isArray(this.features) ? this.features : [];
    if (
      this._blockViews === views &&
      this._blockFeatures === this.features &&
      this._blockExpired === this.expired
    ) {
      return this._blocks;
    }
    this._blockViews = views;
    this._blockFeatures = this.features;
    this._blockExpired = this.expired;

    const drawn = features.length > 0;
    this._blocks = views.map((view): AisObservationBlock => {
      const matches = matchAisObservation(view, features);
      return {
        view,
        matches,
        empty: matches.length
          ? null
          : drawn
          ? 'nomatch'
          : this.expired
          ? 'closed'
          : 'idle',
      };
    });
    return this._blocks;
  }

  /** How many vessels answer any observation — the header's count pill. */
  get foundCount(): number {
    return this.blocks.reduce((n, b) => n + b.matches.length, 0);
  }

  /** How many vessels were searched, for the "not seen" message. */
  get searchedCount(): number {
    return Array.isArray(this.features) ? this.features.length : 0;
  }

  /**
   * The header's chip. A task carries one observation — `tipEngine` reads
   * `task.observation[0]` and nothing else — so the single case is THE case,
   * and the chip states it. More than one is a data shape the panel should not
   * misreport, so it counts them instead and each block states its own.
   */
  get headerChip(): AisObservationView | null {
    const views = this.views;
    return views.length === 1 ? views[0] : null;
  }

  get headerCountLabel(): string {
    const views = this.views;
    return views.length === 1 ? `${this.foundCount}` : `${views.length} observations`;
  }

  /** Accent state: something on the map answers what the task is watching for. */
  get isHit(): boolean {
    return this.foundCount > 0;
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /**
   * The observation the centre button frames, or null when none has a shape.
   *
   * The FIRST with geometry rather than a per-block control: a task carries one
   * observation (`tipEngine` reads `task.observation[0]`), and the button lives
   * in the header, which has one slot and cannot ask "which one".
   */
  get areaView(): AisObservationView | null {
    return this.views.find((v) => v.hasGeometry) ?? null;
  }

  /**
   * The toggle is icon-only — 22px on a header band that already carries a chip,
   * a count and a caret — so the tooltip is the whole of its label and has to
   * name the ACT, not the state. "On the map" would leave a click that does the
   * opposite of what the control reads as.
   */
  get onMapTooltip(): string {
    return this.onMap
      ? 'Take these vessels off the map'
      : 'Show the matching vessels on the map';
  }

  centre(event: Event): void {
    event.stopPropagation();
    const view = this.areaView;
    if (view) this.centreArea.emit(view);
  }

  toggleMap(event: Event): void {
    event.stopPropagation();
    this.toggleOnMap.emit();
  }

  isActive(match: AisObservationMatch): boolean {
    return !!this.activeMmsi && this.activeMmsi === match.mmsi;
  }

  /** Row click = centre and select, like a Watched Vessels row's Locate. */
  pick(block: AisObservationBlock, match: AisObservationMatch): void {
    this.focusVessel.emit({ observation: block.view.data, match });
  }

  focusRow(block: AisObservationBlock, match: AisObservationMatch, event: Event): void {
    event.stopPropagation();
    this.pick(block, match);
  }

  browse(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.browseSnapshots.emit();
  }

  trackByObservation(_i: number, block: AisObservationBlock): string {
    return block.view.id;
  }

  trackByMatch(_i: number, match: AisObservationMatch): string {
    return match.mmsi || `${match.lat},${match.lng}`;
  }
}
